import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useAuthStore from "@/stores/user/authStore";
import useEditProfileModalStore from "@/stores/ui/editProfileModalStore";
import { useProfile } from "@/hooks/useProfile";
import AccountSkeleton from "../components/skeletons/AccountSkeleton";
import userService from "@/services/userService";
import { useToast } from "@/hooks/use-toast";
import { useUserConnections } from "@/hooks/useUserConnections";
import { Connection, ConnectionsData } from "@/types/social";
import { SimilarUser } from "@/types/user";
import logger from "@/utils/logger";

// Import modular components from the index file
import {
  ProfileContent,
  EditProfileModal,
  ConnectionsModal,
  BackgroundSelectionModal,
  AccountHeader,
  CropImageModal,
} from "@/components/social/profile-page";

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

const Account: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const {
    user,
    profile_image: currentUserProfileImage,
    setProfileImage,
    background_image: currentUserBackgroundImage,
    setBackgroundImage,
  } = useAuthStore();
  const { toast } = useToast();
  const { openModal, isOpen, closeModal } = useEditProfileModalStore();

  const userId = id || user?.user_id;
  const navigate = useNavigate();
  const profileInputRef = useRef<HTMLInputElement>(null);
  const backgroundInputRef = useRef<HTMLInputElement>(null);

  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [connectionsModalOpen, setConnectionsModalOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [imageType, setImageType] = useState<"profile" | "background">(
    "profile"
  );
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(
    null
  );
  const [isSaving, setIsSaving] = useState(false);
  const [activeConnectionsTab, setActiveConnectionsTab] = useState<
    "followers" | "following"
  >("followers");
  const [isBackgroundModalOpen, setIsBackgroundModalOpen] = useState(false);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);

  const {
    profile,
    isLoading: isProfileLoading,
    isCurrentUser,
    similarUsers: initialSimilarUsers,
    followUser,
    unfollowUser,
  } = useProfile(userId || null);

  // Add loading state for follow/unfollow
  const [localProfile, setLocalProfile] = useState<typeof profile>(null);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [localSimilarUsers, setLocalSimilarUsers] = useState<SimilarUser[]>([]);

  // Add connections state
  const {
    connections: initialConnections,
    isLoading: isConnectionsLoading,
    refetch: refetchConnections,
  } = useUserConnections(userId || null);
  const [connections, setConnections] = useState<ConnectionsData | null>(null);

  // Update localProfile when profile changes
  useEffect(() => {
    if (profile) {
      logger.log("Profile:", profile);
      setLocalProfile(profile);
    }
  }, [profile]);

  // Update connections when initialConnections changes
  useEffect(() => {
    if (initialConnections) {
      logger.log("Initial connections:", initialConnections);
      setConnections(initialConnections);
    }
  }, [initialConnections]);

  // Update localSimilarUsers whsen initialSimilarUsers changes
  useEffect(() => {
    if (initialSimilarUsers && !isProfileLoading) {
      setLocalSimilarUsers(initialSimilarUsers);
    }
  }, [initialSimilarUsers, isProfileLoading]);

  const onCropComplete = useCallback(
    (croppedArea: any, croppedAreaPixels: any) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.src = url;
    });

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: CropArea
  ): Promise<File> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("No 2d context");
    }

    // Set canvas size to match the crop area
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Draw the cropped image
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    // Convert canvas to blob
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        resolve(new File([blob], "cropped-image.jpg", { type: "image/jpeg" }));
      }, "image/jpeg");
    });
  };

  const handleImageUpload = async (
    file: File,
    type: "profile" | "background"
  ) => {
    try {
      if (type === "profile") {
        const response = await userService.updateProfileImage(file);
        if (response.success && user) {
          setProfileImage(response.profile_image);
        }
      } else {
        const response = await userService.updateBackgroundImage(file);
        if (response.success && user) {
          setBackgroundImage(response.background_image);
        }
      }
      toast({
        title: "Image Updated",
        description: `${
          type === "profile" ? "Profile" : "Background"
        } image updated successfully`,
        duration: 2500,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to update ${type} image`,
        duration: 2500,
        variant: "destructive",
      });
    }
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: "profile" | "background"
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageType(type);
      const reader = new FileReader();
      reader.onload = () => {
        setImageToCrop(reader.result as string);
        setCropModalOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleModalClose = () => {
    setCropModalOpen(false);
    setImageToCrop(null);
    setCroppedAreaPixels(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    if (profileInputRef.current) profileInputRef.current.value = "";
    if (backgroundInputRef.current) backgroundInputRef.current.value = "";
  };

  const handleCropSave = async () => {
    if (imageToCrop && croppedAreaPixels) {
      try {
        setIsSaving(true);
        const croppedImage = await getCroppedImg(
          imageToCrop,
          croppedAreaPixels
        );
        await handleImageUpload(croppedImage, imageType);
        handleModalClose();
        toast({
          title: "Image Updated",
          description: `${
            imageType === "profile" ? "Profile" : "Background"
          } image updated successfully`,
          duration: 2500,
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Error saving cropped image",
          duration: 2500,
          variant: "destructive",
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  // Handle follow/unfollow with optimistic updates
  const handleFollowToggle = async () => {
    logger.log("Local profile:", localProfile);
    if (!localProfile) {
      logger.log("No local profile found, returning");
      return;
    }
    logger.log("Starting follow toggle operation");
    setIsFollowLoading(true);

    // Store current state for potential revert
    const previousState = { ...localProfile };
    logger.log("Previous state:", previousState);

    const isUnfollowing =
      localProfile.is_following || localProfile.is_requested;
    logger.log("Is unfollowing:", isUnfollowing);

    // Optimistically update UI
    setLocalProfile((prev) => {
      if (!prev) return prev;
      logger.log("Updating local profile state");
      const newState = {
        ...prev,
        is_following: isUnfollowing ? false : !prev.is_private,
        is_requested: !isUnfollowing && prev.is_private,
        follower_count: isUnfollowing
          ? prev.follower_count - (prev.is_following ? 1 : 0)
          : prev.follower_count + (!prev.is_private ? 1 : 0),
      };
      logger.log("New state:", newState);
      return newState;
    });

    // Update connections state if we're following a user
    if (connections && !isUnfollowing && localProfile) {
      // Capture the current localProfile value to avoid dependency on changing state
      const currentProfile = { ...localProfile };

      // Add the user to the following list if they're not already there
      const isAlreadyFollowing =
        connections.following && connections.following.length > 0
          ? connections.following.some(
              (conn) => conn.user_id === currentProfile.user_id
            )
          : false;

      if (!isAlreadyFollowing) {
        setConnections((prev) => {
          if (!prev) return prev;

          const newConnection: Connection = {
            user_id: user?.user_id || "",
            name: user?.name || "",
            username: user?.username || "",
            profile_image: currentUserProfileImage || "",
            is_following: !currentProfile.is_private,
            is_requested: currentProfile.is_private,
            is_private: user?.is_private || false,
          };

          return {
            ...prev,
            followers: prev.followers
              ? [...prev.followers, newConnection]
              : [newConnection],
          };
        });
      }
    } else if (connections && isUnfollowing && localProfile) {
      // Remove the user from the following list
      setConnections((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          followers: prev.followers
            ? prev.followers.filter((conn) => conn.user_id !== user?.user_id)
            : [],
        };
      });
    }

    try {
      if (isUnfollowing) {
        logger.log("Attempting to unfollow user:", previousState.user_id);
        await unfollowUser(previousState.user_id);
        // Close connections modal if we're unfollowing a private user
        if (previousState.is_private) {
          logger.log("Closing connections modal for private user");
          setConnectionsModalOpen(false);
        }
      } else {
        logger.log("Attempting to follow user:", previousState.user_id);
        await followUser(previousState.user_id);
      }
      logger.log("Follow/unfollow operation successful");
    } catch (error) {
      console.error("Follow/unfollow operation failed:", error);
      // Revert to previous state on error
      setLocalProfile(previousState);

      // Also revert connections state
      if (connections) {
        if (!isUnfollowing) {
          // Capture the current localProfile value to avoid dependency on changing state
          const currentProfile = { ...localProfile };

          // Remove the user from the following list if follow failed
          setConnections((prev) => {
            if (!prev) return prev;

            return {
              ...prev,
              following: prev.following.filter(
                (conn) => conn.user_id !== currentProfile.user_id
              ),
            };
          });
        } else {
          // Capture the current localProfile value to avoid dependency on changing state
          const currentProfile = { ...localProfile };

          // Add the user back to the following list if unfollow failed
          const isAlreadyFollowing = connections.following.some(
            (conn) => conn.user_id === currentProfile.user_id
          );

          if (!isAlreadyFollowing && currentProfile) {
            setConnections((prev) => {
              if (!prev) return prev;

              const newConnection: Connection = {
                user_id: currentProfile.user_id,
                name: currentProfile.name,
                username: currentProfile.username,
                profile_image: currentProfile.profile_image,
                is_following: true,
                is_requested: false,
                is_private: currentProfile.is_private,
              };

              return {
                ...prev,
                following: [...prev.following, newConnection],
              };
            });
          }
        }
      }

      toast({
        title: "Error",
        description: "Failed to update follow status",
        duration: 2500,
        variant: "destructive",
      });
    } finally {
      logger.log("Completing follow toggle operation");
      setIsFollowLoading(false);
    }
  };

  // Update connections when a user is followed/unfollowed from Similar Travelers
  const handleSimilarUserFollowChange = (
    userId: string,
    isFollowing: boolean,
    isPrivate: boolean,
    userData?: SimilarUser
  ) => {
    // Update localProfile stats when a user is followed/unfollowed from the Similar Travelers list
    if (localProfile && isCurrentUser) {
      setLocalProfile((prev) => {
        if (!prev || isPrivate) return prev;

        return {
          ...prev,
          following_count: isFollowing
            ? prev.following_count + 1
            : prev.following_count - 1,
        };
      });
    }

    // Update the similar users list
    setLocalSimilarUsers((prev) => {
      return prev.map((user) =>
        user.user_id === userId
          ? {
              ...user,
              is_following: isFollowing && !isPrivate,
              is_requested: isFollowing && isPrivate,
            }
          : user
      );
    });

    // Update connections list
    if (connections && isFollowing) {
      // If userData is provided, use it directly instead of finding in localSimilarUsers
      const similarUser =
        userData || localSimilarUsers.find((u) => u.user_id === userId);

      if (similarUser) {
        // Add the user to the following list if they're not already there
        const isAlreadyFollowing =
          connections.following &&
          connections.following.some((conn) => conn.user_id === userId);

        if (!isAlreadyFollowing) {
          setConnections((prev) => {
            if (!prev) return prev;

            const newConnection: Connection = {
              user_id: similarUser.user_id,
              name: similarUser.name,
              username: similarUser.username,
              profile_image: similarUser.profile_image,
              is_following: !isPrivate,
              is_requested: isPrivate,
              is_private: isPrivate,
            };

            return {
              ...prev,
              following: [...prev.following, newConnection],
            };
          });
        }
      }
    } else if (connections && !isFollowing) {
      // Remove the user from the following list
      setConnections((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          following: prev.following.filter((conn) => conn.user_id !== userId),
        };
      });
    }
  };

  // Handle unfollows from the ConnectionsModal
  const handleConnectionUnfollow = (userId: string) => {
    // Update the following count in the profile
    if (localProfile && isCurrentUser) {
      setLocalProfile((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          following_count: prev.following_count - 1,
        };
      });
    }

    // Update the similar users list if the unfollowed user is in it
    setLocalSimilarUsers((prev) => {
      return prev.map((user) =>
        user.user_id === userId
          ? { ...user, is_following: false, is_requested: false }
          : user
      );
    });
  };

  // Add a function to handle background selection from the modal
  const handleBackgroundSelected = async (backgroundUrl: string) => {
    try {
      // Call the API to update the background image URL
      const response = await userService.updateBackgroundImageUrl(
        backgroundUrl
      );

      if (response.success && user) {
        // Update the local profile state
        setBackgroundImage(response.background_image);

        toast({
          title: "Background Updated",
          description: "Your background has been updated successfully",
          duration: 2500,
        });

        // Close the modal after successful update
        setIsBackgroundModalOpen(false);
        setIsBackgroundLoading(false);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update background image",
        duration: 2500,
        variant: "destructive",
      });
    } finally {
      setIsBackgroundLoading(false);
    }
  };

  // Handle connections tab click
  const handleConnectionsClick = (tab: "followers" | "following") => {
    setConnectionsModalOpen(true);
    setActiveConnectionsTab(tab);
  };

  if (!user) {
    navigate("/login");
    return null;
  }

  if (
    isProfileLoading ||
    !currentUserBackgroundImage ||
    !currentUserProfileImage ||
    !localProfile
  ) {
    return <AccountSkeleton />;
  }

  return (
    <>
      <div className="flex flex-col w-full h-full max-h-full">
        {/* Hidden file input elements */}
        <input
          type="file"
          ref={profileInputRef}
          className="hidden"
          accept="image/*"
          onChange={(e) => handleFileChange(e, "profile")}
        />
        <input
          type="file"
          ref={backgroundInputRef}
          className="hidden"
          accept="image/*"
          onChange={(e) => handleFileChange(e, "background")}
        />

        {/* Use the AccountHeader component */}
        <AccountHeader
          profileImage={
            isCurrentUser
              ? currentUserProfileImage || ""
              : profile?.profile_image || ""
          }
          backgroundImage={
            isCurrentUser
              ? currentUserBackgroundImage || ""
              : profile?.background_image || ""
          }
          name={isCurrentUser ? user?.name || "" : profile?.name || ""}
          username={
            isCurrentUser ? user?.username || "" : profile?.username || ""
          }
          isPrivate={!!profile?.is_private}
          isCurrentUser={isCurrentUser}
          isFollowing={!!localProfile?.is_following}
          isRequested={!!localProfile?.is_requested}
          followerCount={localProfile?.follower_count || 0}
          followingCount={localProfile?.following_count || 0}
          isFollowLoading={isFollowLoading}
          isBackgroundLoading={isBackgroundLoading}
          onEditProfile={() => openModal()}
          onFollowToggle={handleFollowToggle}
          onProfileImageClick={() => profileInputRef.current?.click()}
          onOpenBackgroundModal={() => setIsBackgroundModalOpen(true)}
          onConnectionsClick={handleConnectionsClick}
        />

        <ProfileContent
          profile={profile}
          similarUsers={localSimilarUsers}
          onFollowStatusChange={handleSimilarUserFollowChange}
        />
      </div>

      {/* Use the CropImageModal component */}
      <CropImageModal
        open={cropModalOpen}
        onClose={handleModalClose}
        imageToCrop={imageToCrop}
        imageType={imageType}
        crop={crop}
        setCrop={setCrop}
        zoom={zoom}
        setZoom={setZoom}
        onCropComplete={onCropComplete}
        onSave={handleCropSave}
        isSaving={isSaving}
      />

      {/* Use the imported modular components */}
      <EditProfileModal isOpen={isOpen} onClose={closeModal} />

      <ConnectionsModal
        isOpen={connectionsModalOpen}
        onClose={() => {
          setConnectionsModalOpen(false);
          if (isCurrentUser) {
            refetchConnections();
          }
        }}
        isPrivate={profile?.is_private}
        isCurrentUser={isCurrentUser}
        activeTab={activeConnectionsTab}
        setActiveTab={setActiveConnectionsTab}
        isFollowing={localProfile?.is_following || false}
        connections={connections}
        setConnections={setConnections}
        isLoading={isConnectionsLoading}
        onUnfollow={handleConnectionUnfollow}
      />

      <BackgroundSelectionModal
        isOpen={isBackgroundModalOpen}
        onClose={() => setIsBackgroundModalOpen(false)}
        onBackgroundSelected={handleBackgroundSelected}
        setIsBackgroundLoading={setIsBackgroundLoading}
      />
    </>
  );
};

export default Account;
